import { useEffect, useState } from 'react'
import styled from 'styled-components'
import { Link } from 'react-router-dom'
import { axiosPost } from '../../Common/Axios'
import type { Test } from '../../../models/Test'
import { TestQuestion } from '../../../models/TestQuestion'
import { TestQuestionAnswer } from '../../../models/TestQuestionAnswer'
import { Result } from '../../../models/Dto/Result'
import { ApiResult } from '../../../models/Enums'
import { TestResult } from '../../../models/TestResult'
import Job from '../../Common/Job'
import { RootState, useAppDispatch, useAppSelector } from '../../../store'
import { TestQuestionAnswerCase } from '../../../models/TestQuestionAnswerCase'
import { registerInventory, unregisterInventory } from '../../Common/CommonApi'
import toast from 'react-hot-toast'
import { setInInventory, setOutInventory } from '../../../store/mainSlice'
import {
  BlueButton,
  ExpandedProps,
  GrayButton,
  GreenButton,
  PurpleButton,
  SmallBlueButton,
  SmallLightGrayButton,
  TomatoButton,
} from '../../Layout/CommonStyle'

// MBTI 검사하러가기
// https://www.16personalities.com/ko
type CountProps = {
  count: string
}
type ProgressItemProps = {
  current?: string
}

type ImageProps = {
  current?: string
  imagePath1?: string
  imagePath2?: string
  imageCnt?: number
  width?: string
}

type Props = {
  testId: number
}

function TestForm(props: Props) {
  const [test, setTest] = useState<Test | null>(null)
  const [testQuestions, setTestQuestions] = useState<TestQuestion[]>([])
  const [questionIndex, setQuestionIndex] = useState<number>(0)
  const [selectedAnswer, setSelectedAnswer] = useState<number[]>([])
  const [testQuestionAnswerCase, setTestQuestionAnswerCase] =
    useState<TestQuestionAnswerCase | null>(null)
  const [testResults, setTestResults] = useState<TestResult[]>([])
  const [hasResult, setHasResult] = useState<boolean>(false)
  const [isExpanded, setIsExpanded] = useState<boolean>(false)

  const dispatch = useAppDispatch()
  const me = useAppSelector((state: RootState) => state.user.me)
  const jobs = useAppSelector((state: RootState) => state.main.jobs)

  useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-extra-semi
    ;(async () => {
      if (props.testId === 0) {
        return
      }
      const payload = { id: props.testId }

      const result = (await axiosPost(
        '/api/test/get_test_info',
        payload
      )) as Result

      if (result.apiResult !== ApiResult.SUCCESS) {
        console.log('get_test_users error', result.message)
        return
      }

      const test = result.data['test']
      const testQuestions = result.data['testQuestions']
      const testQuestionAnswers = result.data['testQuestionAnswers']
      const testQuestionAnswerCase = result.data[
        'testQuestionAnswerCase'
      ] as TestQuestionAnswerCase
      const testResults = result.data['testResults'] as TestResult[]

      const newTestResults = testResults.map((testResult) => {
        const job = jobs.find((job) => job.id === testResult.jobId)!
        return { ...testResult, job } as TestResult
      })

      // answer를 question.answers로 분배
      testQuestions.forEach((testQuestion: TestQuestion) => {
        testQuestion.answers = testQuestionAnswers.filter(
          (testQuestionAnswer: TestQuestionAnswer) =>
            testQuestionAnswer.questionId === testQuestion.id
        )
      })

      const answers = getAnswerArray(
        testQuestions.length,
        testQuestionAnswerCase
      )
      setSelectedAnswer(answers)

      setTest(test)
      setTestQuestions(testQuestions)
      setTestQuestionAnswerCase(testQuestionAnswerCase)
      setTestResults(newTestResults)

      setHasResult(newTestResults.length > 0)
      setQuestionIndex(0)
    })()
  }, [props.testId])

  // TestQuestionAnswerCase -> answer[]로 변환
  const getAnswerArray = (
    questionCount: number,
    testQuestionAnswerCase: TestQuestionAnswerCase
  ): number[] => {
    const answers: number[] = []

    if (questionCount > 0) {
      answers.push(testQuestionAnswerCase.questionAnswerId1)
    }
    if (questionCount > 1) {
      answers.push(testQuestionAnswerCase.questionAnswerId2)
    }
    if (questionCount > 2) {
      answers.push(testQuestionAnswerCase.questionAnswerId3)
    }
    if (questionCount > 3) {
      answers.push(testQuestionAnswerCase.questionAnswerId4)
    }
    if (questionCount > 4) {
      answers.push(testQuestionAnswerCase.questionAnswerId5)
    }
    if (questionCount > 5) {
      answers.push(testQuestionAnswerCase.questionAnswerId6)
    }
    if (questionCount > 6) {
      answers.push(testQuestionAnswerCase.questionAnswerId7)
    }
    if (questionCount > 7) {
      answers.push(testQuestionAnswerCase.questionAnswerId8)
    }
    if (questionCount > 8) {
      answers.push(testQuestionAnswerCase.questionAnswerId9)
    }
    if (questionCount > 9) {
      answers.push(testQuestionAnswerCase.questionAnswerId10)
    }
    return answers
  }

  // 문제의 답변 클릭
  const handleAnswerClick = (answer: TestQuestionAnswer) => {
    const newSelectedAnswer = [...selectedAnswer]
    newSelectedAnswer[questionIndex] = answer.id
    setSelectedAnswer(newSelectedAnswer)
  }
  const handlePrevQuestion = () => {
    if (questionIndex > 0) {
      setQuestionIndex(questionIndex - 1)
    }
  }

  const handleNextQuestion = () => {
    if (questionIndex < testQuestions.length - 1) {
      setQuestionIndex(questionIndex + 1)
    }
  }

  // 결과보기
  const handleShowResult = async () => {
    const payload = {
      testId: props.testId,
      answers: selectedAnswer,
    }

    const result = await axiosPost('/api/test/get_test_result', payload)
    if (result.apiResult !== ApiResult.SUCCESS) {
      console.log('get_test_users error', result.message)
      return
    }

    const _testResults = result.data['testResults'] as TestResult[]
    const _testQuestionAnswerCase = result.data[
      'testQuestionAnswerCase'
    ] as TestQuestionAnswerCase

    const newTestResults = _testResults.map((testResult) => {
      const job = jobs.find((job) => job.id === testResult.jobId)!
      return { ...testResult, job } as TestResult
    })

    setTestResults(newTestResults)
    setTestQuestionAnswerCase(_testQuestionAnswerCase)
    setHasResult(true)
  }

  // 테스트 다시하기
  const handleRetryTest = async () => {
    setQuestionIndex(0)
    setHasResult(false)
  }

  // 인벤토리에 추가 삭제
  const handleRegisterInventory = async (jobId: number, isAdd: boolean) => {
    let success = false

    if (isAdd) {
      success = await registerInventory(jobId, me.id, dispatch)
    } else {
      if (confirm('인벤토리에서 제거하시겠습니까?') === false) {
        return
      }
      success = await unregisterInventory(jobId, me.id, dispatch)
    }

    if (success) {
      setTestResults(
        testResults.map((testResult) =>
          testResult.job.id === jobId
            ? {
                ...testResult,
                job: { ...testResult.job, isInInventory: isAdd },
              }
            : testResult
        )
      )
    }
  }

  return (
    <Container>
      {!test ? (
        <NoTest>
          <div>테스트가 존재하지 않습니다.</div>
          <div>
            <Link to="/mentee/test/">리스트로 이동</Link>
          </div>
        </NoTest>
      ) : (
        <>
          <Header>
            <TestName>{test?.name}</TestName>
            <TestSubName>{test?.subName}</TestSubName>
          </Header>

          {!hasResult ? (
            <Content>
              <Progress count={testQuestions.length.toString()}>
                {testQuestions.map((testQuestion, index) => (
                  <ProgressItem
                    key={testQuestion.id}
                    current={index === questionIndex ? 'current' : undefined}
                  />
                ))}
              </Progress>
              <TestDiv>
                <Question>
                  {testQuestions.length > questionIndex && (
                    <>{testQuestions[questionIndex].content}</>
                  )}
                  {Boolean(testQuestions[questionIndex].information) && (
                    <Information>
                      {Boolean(testQuestions[questionIndex].informationUrl) ? (
                        <a
                          target="_blank"
                          href={testQuestions[questionIndex].informationUrl}
                        >
                          {testQuestions[questionIndex].information}
                        </a>
                      ) : (
                        <span>{testQuestions[questionIndex].information}</span>
                      )}
                    </Information>
                  )}
                </Question>
                <Answers
                  count={testQuestions[questionIndex].columnCnt.toString()}
                >
                  {testQuestions.length > questionIndex &&
                    testQuestions[questionIndex].answers.map((answer) => (
                      <AnswerDiv
                        onClick={() => handleAnswerClick(answer)}
                        imageCnt={Boolean(answer.imagePath) ? 1 : 0}
                        current={
                          answer.id === selectedAnswer[questionIndex]
                            ? 'current'
                            : undefined
                        }
                      >
                        <img
                          src={`/tests/${test.id}/${answer.imagePath}`}
                          alt={answer.content}
                        />
                        {testQuestions[questionIndex].isShowContent && (
                          <Title>{answer.content}</Title>
                        )}
                      </AnswerDiv>
                    ))}
                </Answers>
              </TestDiv>
            </Content>
          ) : (
            <ResultContent>
              <JobResultList>
                <ResultHeader>테스트 결과</ResultHeader>
                <Factor>
                  <FactorTitle>{me.username}님의 주요 키워드</FactorTitle>
                  <TagContainer isExpanded={isExpanded}>
                    {testQuestionAnswerCase?.tags &&
                      JSON.parse(testQuestionAnswerCase?.tags).map(
                        (tag: string) => <Tag>{tag}</Tag>
                      )}
                  </TagContainer>
                  <More>
                    {isExpanded ? (
                      <SmallLightGrayButton
                        onClick={() => setIsExpanded(false)}
                      >
                        접기
                      </SmallLightGrayButton>
                    ) : (
                      <SmallLightGrayButton onClick={() => setIsExpanded(true)}>
                        더보기
                      </SmallLightGrayButton>
                    )}
                  </More>
                </Factor>
                <ResultDescription>
                  {' '}
                  {me.username}님의 테스트 결과에의해 PathFinder가 추천해드린
                  직업입니다.
                </ResultDescription>
                {testResults.map((testResult, index) => (
                  <Job
                    key={index}
                    id={testResult.job.id}
                    name={testResult.job.name}
                    image={testResult.job.photo}
                    description={testResult.job.overview}
                    isInInventory={testResult.job.isInInventory}
                    onChangeToInventory={handleRegisterInventory}
                  />
                ))}
              </JobResultList>
            </ResultContent>
          )}

          <Toolbar>
            <BlueButton
              disabled={questionIndex <= 0}
              onClick={() => handlePrevQuestion()}
              style={{
                display: hasResult ? 'none' : 'block',
              }}
            >
              이전
            </BlueButton>
            <BlueButton
              disabled={
                questionIndex >= testQuestions.length - 1 ||
                selectedAnswer[questionIndex] === 0 ||
                hasResult
              }
              style={{
                display: hasResult ? 'none' : 'block',
              }}
              onClick={() => handleNextQuestion()}
            >
              다음
            </BlueButton>
            <TomatoButton
              disabled={
                questionIndex < testQuestions.length - 1 ||
                selectedAnswer[questionIndex] === 0 ||
                hasResult
              }
              style={{
                display:
                  questionIndex < testQuestions.length - 1 ||
                  selectedAnswer[questionIndex] === 0 ||
                  hasResult
                    ? 'none'
                    : 'block',
              }}
              onClick={() => handleShowResult()}
            >
              결과보기
            </TomatoButton>
            <BlueButton
              style={{
                display: !hasResult ? 'none' : 'block',
              }}
              onClick={() => handleRetryTest()}
            >
              테스트 다시하기
            </BlueButton>
          </Toolbar>
        </>
      )}
    </Container>
  )
}

const Container = styled.div`
  grid-area: main;
  margin-bottom: 1rem;
`
const NoTest = styled.div`
  padding: 40px;
  font-size: 1.5rem;
  text-align: center;
  a {
    padding-top: 1.5rem;
    font-size: 1.3rem;
    color: blue;
  }
`
const Header = styled.div`
  position: relative;
  background-color: var(--point-color3);
  padding: 1rem;
`
const TestInfo = styled.div`
  position: absolute;
  top: 10px;
  left: 10px;
  color: #fdfdfd;
  padding: 3px 15px;
  background-color: var(--point-color2);
`
const TestName = styled.div`
  color: white;
  font-size: 1.5rem;
  text-align: center;
`
const TestSubName = styled.div`
  padding-top: 10px;
  color: white;
  font-size: 1rem;
  text-align: center;
`
const Information = styled.div`
  font-size: 0.9rem;
  text-align: right;
`

const Content = styled.div`
  min-height: 500px;
  margin-top: 20px;
  background-color: #fffaf6;
  border: 1px solid var(--point-color3);
`

const ResultContent = styled.div`
  padding: 20px;
`

const ResultHeader = styled.div`
  padding-top: 20px;
  font-size: 1.5rem;
  text-align: center;
`

const ResultDescription = styled.div`
  padding: 10px 0 30px;
  font-size: 1.05rem;
  text-align: center;
  color: #555;
`

const Progress = styled.div`
  background-color: transparent;
  display: flex;
  flex-direction: row;
  justify-content: center;
  column-gap: 10px;
  margin: 10px 80px 0;
  & > div {
    width: ${(props: CountProps) => `${100 / (parseInt(props.count) + 1)}%`};
  }
`

const ProgressItem = styled.div`
  background-color: ${(props: ProgressItemProps) =>
    props.current === 'current' ? 'var(--point-color3)' : 'white'};
  border: 1px solid var(--point-color3);
  height: 10px;
  width: 100px;
`
const TestDiv = styled.div`
  padding: 40px 120px;
  min-height: 500px;

  @media (max-width: 768px) {
    padding: 10px;
  }
`
const Question = styled.div`
  text-align: left;
  font-size: 1.5rem;
  padding: 20px 0 40px;
  color: var(--black-color);

  @media (max-width: 768px) {
    padding: 20px 20px 40px;
    text-align: justify;
  }
`
const Answers = styled.div`
  display: grid;
  grid-template-columns: ${(props: CountProps) =>
    `repeat(${props.count}, 1fr)`};
  // flex-wrap: wrap;
  // justify-content: center;
  // column-gap: 20px;
  // row-gap: 20px;
  // text-align: left;
  // font-size: 1.2rem;
  // font-weight: 650;
  // color: var(--black-color);
`
// const AnswerWrap = styled.div`
//   width: ${(props: ImageProps) => props.width};
// `

const AnswerDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px 10px 20px;
  background-color: ${(props: ProgressItemProps) =>
    props.current === 'current' ? 'tomato' : 'var(--point-color5)'};

  color: ${(props: ProgressItemProps) =>
    props.current === 'current' ? 'white' : 'black'};

  img:nth-child(1) {
    width: 100%;
    display: ${(props: ImageProps) =>
      props.imageCnt !== undefined && props.imageCnt >= 1 ? 'block' : 'none'};

    filter: ${(props: ProgressItemProps) =>
      props.current === 'current' ? 'grayscale(0)' : 'grayscale(40%)'};

    background-color: ${(props: ProgressItemProps) =>
      props.current === 'current' ? 'tomato' : '#fcf0db'};
  }

  img:nth-child(2) {
    display: none;
    /* (props: ImageProps) =>
      props.imageCnt !== undefined && props.imageCnt = 2 ? 'block' : 'none'}; */

    filter: ${(props: ProgressItemProps) =>
      props.current === 'current' ? 'grayscale(0)' : 'grayscale(40%)'};
  }

  &:hover {
    background-color: ${(props: ProgressItemProps) =>
      props.current === 'current' ? 'tomato' : '#fcf0db'};
    color: ${(props: ProgressItemProps) =>
      props.current === 'current' ? 'white' : '#555'};

    img:nth-child(1) {
      display: ${(props: ImageProps) =>
        props.imageCnt !== undefined && props.imageCnt === 1
          ? 'block'
          : 'none'};

      filter: ${(props: ProgressItemProps) =>
        props.current === 'current' ? 'grayscale(0)' : 'grayscale(40%)'};

      background-color: ${(props: ProgressItemProps) =>
        props.current === 'current' ? 'tomato' : '#fcf0db'};
    }

    img:nth-child(2) {
      display: ${(props: ImageProps) =>
        props.imageCnt !== undefined && props.imageCnt === 2
          ? 'block'
          : 'none'};

      filter: ${(props: ProgressItemProps) =>
        props.current === 'current' ? 'grayscale(0)' : 'grayscale(40%)'};
    }
  }
`

const Title = styled.div`
  padding-top: 10px;
  font-size: 1.2rem;
  text-align: left;
  white-space: pre-line;
`

const Toolbar = styled.div`
  padding: 10px 0 30px;
  display: flex;
  flex-direction: row;
  justify-content: center;
  column-gap: 20px;
`

const JobResultList = styled.div``

const Factor = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  flex-wrap: wrap;
  margin-bottom: 20px;
`
const FactorTitle = styled.div`
  width: 100%;
  font-size: 1.05rem;
`
const Tag = styled.div`
  background-color: #ecba5d;
  margin: 2px;
  padding: 3px 12px;
  border-radius: 10px;
`
const TagContainer = styled.div`
  max-height: 60px;
  overflow: hidden;
  position: relative;

  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  flex-wrap: wrap;

  ${(props: ExpandedProps) =>
    props.isExpanded && 'max-height: 1000px; padding-bottom:10px;'}

  &::after {
    content: '';
    display: block;
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 30px;
    background: linear-gradient(
      to bottom,
      rgba(255, 255, 255, 0),
      rgba(255, 255, 255, 1)
    );
  }
`
const More = styled.div`
  text-align: right;
  width: 100%;
`

export default TestForm
